/**
 * Dialogue Propositions IA — Rapprochement Wave intelligent.
 * Affiche les propositions du LLM (confiance + justification), l'utilisateur
 * coche celles qu'il valide puis les applique en un clic.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fxof = (v) => (v == null || isNaN(v)) ? '—' : `${Math.round(v).toLocaleString('fr-FR')} XOF`;

const ConfidenceBadge = ({ value }) => {
  const cls = value >= 85 ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : value >= 60 ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-zinc-100 text-zinc-600 border-zinc-200';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold tabular-nums ${cls}`}>
      {value}%
    </span>
  );
};

const WaveAiProposalsDialog = ({ open, onClose, result, analyzing, onApplied }) => {
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const proposals = useMemo(() => result?.proposals || [], [result]);

  useEffect(() => {
    // Pré-sélectionne les propositions à confiance élevée (≥ 80)
    if (open && proposals.length > 0) {
      setSelected(new Set(proposals.filter(p => p.confidence >= 80).map(p => p.transactionId)));
    }
  }, [open, proposals]);

  const toggle = (txId) => {
    const next = new Set(selected);
    if (next.has(txId)) next.delete(txId); else next.add(txId);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === proposals.length) setSelected(new Set());
    else setSelected(new Set(proposals.map(p => p.transactionId)));
  };

  const apply = async () => {
    const items = proposals
      .filter(p => selected.has(p.transactionId))
      .map(p => ({
        transactionId: p.transactionId,
        confidence: p.confidence,
        reason: p.reason,
        allocations: p.allocations.map(a => ({
          operationId: a.operationId,
          allocatedAmount: a.allocatedAmount,
          waveFeeAllocated: 0,
          kind: a.kind || 'total',
        })),
      }));
    if (items.length === 0) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/wave/reconcile/ai/apply`, { items });
      const { appliedCount, errorCount, errors } = r.data;
      toast({
        title: `${appliedCount} rapprochement(s) IA appliqué(s)`,
        description: errorCount > 0 ? `${errorCount} erreur(s) : ${errors.map(e => e.error).join(' · ').slice(0, 150)}` : undefined,
        variant: errorCount > 0 ? 'destructive' : undefined,
      });
      onApplied?.();
      onClose();
    } catch (e) {
      toast({ title: 'Erreur application IA', description: e?.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setApplying(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !applying && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="wave-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            Rapprochement IA — Propositions
            {result?.model && (
              <span className="text-[10px] font-normal text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded">
                {result.provider} · {result.model}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {analyzing ? (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-500 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
            <div className="text-sm font-medium">Analyse IA en cours…</div>
            <div className="text-xs text-zinc-400">Le LLM compare les transactions Wave aux opérations candidates.</div>
          </div>
        ) : result?.error ? (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800 flex gap-2 items-start" data-testid="wave-ai-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {result.error}
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 text-sm" data-testid="wave-ai-empty">
            {result?.message || 'Aucune proposition — les transactions analysées n\'ont pas de correspondance suffisamment fiable.'}
            {result?.analyzed != null && <div className="text-xs text-zinc-400 mt-1">{result.analyzed} transaction(s) analysée(s)</div>}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-zinc-600">
              <label className="flex items-center gap-1.5 cursor-pointer font-medium">
                <input type="checkbox" checked={selected.size === proposals.length && proposals.length > 0}
                       onChange={toggleAll} data-testid="wave-ai-select-all" />
                Tout sélectionner ({selected.size}/{proposals.length})
              </label>
              <span className="text-zinc-400">
                {result.analyzed} analysée(s) · Pré-cochées : confiance ≥ 80%
              </span>
            </div>
            <div className="space-y-2">
              {proposals.map((p) => (
                <div key={p.transactionId}
                     className={`p-3 border rounded-xl transition-colors ${selected.has(p.transactionId) ? 'border-violet-300 bg-violet-50/50' : 'border-zinc-200 bg-white'}`}
                     data-testid={`wave-ai-proposal-${p.waveRef}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1" checked={selected.has(p.transactionId)}
                           onChange={() => toggle(p.transactionId)} data-testid={`wave-ai-check-${p.waveRef}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono text-xs font-semibold">{p.waveRef}</span>
                        <ConfidenceBadge value={p.confidence} />
                        <span className="text-xs text-zinc-600">{p.beneficiaryName || '—'}</span>
                        <span className="text-xs font-semibold tabular-nums text-rose-700 ml-auto">{fxof(p.amountSent)}</span>
                      </div>
                      <div className="text-[11px] text-zinc-600 italic mb-2">« {p.reason} »</div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.allocations.map((a, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 bg-white border border-zinc-200 rounded-lg">
                            <span className="font-mono font-semibold">{a.operationNumber || a.operationId}</span>
                            {a.companyName && (
                              <span className="text-zinc-500 flex items-center gap-0.5">
                                <Building2 className="h-2.5 w-2.5" />{a.companyName}
                              </span>
                            )}
                            <span className="tabular-nums font-medium">{fxof(a.allocatedAmount)}</span>
                            <span className="uppercase text-violet-600 font-bold">{a.kind}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={applying}>Fermer</Button>
          {proposals.length > 0 && !analyzing && (
            <Button onClick={apply} disabled={applying || selected.size === 0}
                    className="bg-violet-600 hover:bg-violet-700" data-testid="wave-ai-apply">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Appliquer {selected.size} rapprochement(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WaveAiProposalsDialog;
